// Commented out line 16 to attempt fix 030826 and line 80 and 81

import React from "react";
import { AppProvider, useApp } from "./context/AppContext";
import { ErrorBoundary } from "./components/ui/ErrorBoundary";
import { MessageSystem } from "./components/ui/ViewLayout";
import { LoadingOverlay } from "./components/ui/LoadingOverlay";
import { LoginView } from "./views/LoginView";
import { MainView } from "./views/MainView";
import { DonorDetailView } from "./views/DonorDetailView";
import { DirectorPortfolioView } from "./views/DirectorPortfolioView";
import { PlaybookView } from "./views/PlaybookView";
import { ReportsView } from "./views/ReportsView";
import { SettingsView } from "./views/SettingsView";
import { CultivationView } from "./views/CultivationView";
import { EventsView } from "./views/EventsView";
import { GiftsView } from "./views/GiftsView";
import { SetPasswordView } from "./views/SetPasswordView";
import { ShowsView } from "./views/ShowsView";
import { CampaignsView } from "./views/CampaignsView";
import { ScholarshipsView } from "./views/ScholarshipsView";
import { DashboardView } from "./views/DashboardView";
import { TasksView } from "./views/TasksView";
import { CapacityView } from "./views/CapacityView";
import { FONT_FAMILY } from "./constants/appConfig";
import { CultivationQueueDrawer } from "./components/cultivation/CultivationQueueDrawer";
import { TaskDrawer } from "./components/tasks/TaskDrawer";
import { QuickTaskBar } from "./components/tasks/QuickTaskBar";
import { ContactReportPrintView } from "./components/reports/ContactReportPrintView";

// ═══════════════════════════════════════════════════════════════
// CLEARVIEW PHILANTHROPY CRM v2.1
// Entry Point: ErrorBoundary → AppProvider → Router → Views
// ═══════════════════════════════════════════════════════════════

const LoadingScreen = () => {
  const { palette } = useApp();
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: palette.background,
        fontFamily: FONT_FAMILY,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          border: "3px solid rgba(255,255,255,0.2)",
          borderTopColor: "#fff",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <p
        style={{
          color: "rgba(255,255,255,0.8)",
          marginTop: 16,
          fontSize: "0.9rem",
        }}
      >
        Loading...
      </p>
    </div>
  );
};

const AppRouter = () => {
  const { currentView, isLoading, can } = useApp();

  if (isLoading) return <LoadingScreen />;

  switch (currentView) {
    case "login":
      return <LoginView />;
    case "setPassword":
      return <SetPasswordView />;
    case "main":
      return <DashboardView />;
    case "donors":
      return <MainView />;
    case "memberDetail":
      return can("memberDetail") ? <DonorDetailView /> : <DashboardView />;
    case "portfolios":
      return can("portfolios") ? <DirectorPortfolioView /> : <DashboardView />;
    case "reports":
      return can("reports") ? <ReportsView /> : <DashboardView />;
    case "cultivation":
      return can("cultivation") ? <CultivationView /> : <DashboardView />;
    case "capacity":
      return can("cultivation") ? <CapacityView /> : <DashboardView />;
    case "events":
      return can("events") ? <EventsView /> : <DashboardView />;
    case "gifts":
      return can("gifts") ? <GiftsView /> : <DashboardView />;
    case "shows":
      return <ShowsView />;
    case "campaigns":
      return can("gifts") ? <CampaignsView /> : <DashboardView />;
    case "scholarships":
      return can("gifts") ? <ScholarshipsView /> : <DashboardView />;
    case "tasks":
      return can("tasks") ? <TasksView /> : <DashboardView />;
    case "playbook":
      return <PlaybookView />;
    case "settings":
      return can("settings") ? <SettingsView /> : <DashboardView />;
    default:
      return <DashboardView />;
  }
};

const GlobalCultivationQueue = () => {
  const { showCultivationQueue, setShowCultivationQueue } = useApp();
  return (
    <CultivationQueueDrawer
      isOpen={showCultivationQueue}
      onClose={() => setShowCultivationQueue(false)}
    />
  );
};

const GlobalTaskDrawer = () => {
  const { showTaskDrawer, setShowTaskDrawer } = useApp();
  return (
    <TaskDrawer
      isOpen={showTaskDrawer}
      onClose={() => setShowTaskDrawer(false)}
    />
  );
};

const GlobalQuickTaskBar = () => {
  const { currentView, showQuickBar, setShowQuickBar } = useApp();

  // Ctrl+K global shortcut
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        // Don't intercept on login/setPassword screens
        if (currentView === "login" || currentView === "setPassword") return;
        e.preventDefault();
        setShowQuickBar(!showQuickBar);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentView, showQuickBar, setShowQuickBar]);

  return (
    <QuickTaskBar
      isOpen={showQuickBar}
      onClose={() => setShowQuickBar(false)}
    />
  );
};

export default function ClearViewPhilanthropyCRM() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <style>{`
          @keyframes slideIn { from { opacity:0; transform:translateX(20px); } to { opacity:1; transform:translateX(0); } }
          @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
          * { box-sizing:border-box; }
          body { margin:0; padding:0; }
          ::-webkit-scrollbar { width:6px; height:6px; }
          ::-webkit-scrollbar-track { background:transparent; }
          ::-webkit-scrollbar-thumb { background:rgba(0,0,0,0.15); border-radius:3px; }
          input[type="checkbox"] { width:16px; height:16px; accent-color:#667eea; cursor:pointer; }
          select { appearance:auto; }
        `}</style>
        <AppRouter />
        <GlobalCultivationQueue />
        <GlobalTaskDrawer />
        <GlobalQuickTaskBar />
        <ContactReportPrintView />
        <MessageSystem />
        <LoadingOverlay />
      </AppProvider>
    </ErrorBoundary>
  );
}
